import { differenceInCalendarDays, format } from 'date-fns';
import styled from '@/styles';
import { DependabilityEvent } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { feedAccentColor } from './tierColors';

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  variants: {
    // the in-card section carries its own 16px bottom padding in the frames
    // (rows already pad 16 below, so the card shows 32px under the last row)
    section: { true: { paddingBottom: '16px' } },
  },
});
const HeaderRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
});
const SectionTitle = styled('div', {
  fontWeight: 700,
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
});
const List = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});
// Row template EL-6c7ee9a4: every row pads 16px below, including the last
const Entry = styled('div', {
  paddingBottom: '16px',
});
const DescriptionRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});
const AccentBar = styled('span', {
  flex: 'none',
  width: '3px',
  height: '18px',
  borderRadius: '100px',
});
const Description = styled('div', {
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
});
const MetaRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '$md',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depMetaText,
});
const MetaDot = styled('span', {
  flex: 'none',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  backgroundColor: dependabilityV2Colors.depMetaText,
});
const EmptyNote = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depMetaText,
});
const ViewMore = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$md',
  alignSelf: 'flex-start',
});

interface Props {
  events: DependabilityEvent[];
  hasMore: boolean;
  loading: boolean;
  onViewMore: () => void;
  // The card feed shows the "Recent activity" header with View more top-right
  // (view-activities frame); inside the activity modal the title bar owns the
  // heading, so the body renders rows only, with pagination at the bottom.
  withHeader?: boolean;
  // A swallowed fetch failure must surface as a retryable note rather than the
  // false "No score activity yet" empty state.
  error?: boolean;
  onRetry?: () => void;
}

// Same check as @/util/date's isTodaysDate — that module can't be imported
// here because it transitively requires App.tsx (and @ionic/react's ESM
// build), which jest cannot parse.
// snake_case ledger enum -> sentence case ("score_initialized" -> "Score
// initialized"). Deliberately generic rather than a lookup table: the backend
// appends new event types as the scoring rules grow, and an unmapped type must
// still read as words, not fall back to the raw identifier.
export const humanizeEventType = (eventType: string): string => {
  const words = (eventType ?? '').replace(/_/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const shortDate = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  if (differenceInCalendarDays(date, new Date()) === 0) return 'Today';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return format(date, sameYear ? 'MMM d' : 'MMM d, yyyy');
};

const ActivityFeed = ({
  events,
  hasMore,
  loading,
  onViewMore,
  withHeader = true,
  error = false,
  onRetry,
}: Props) => {
  const viewMore = hasMore && (
    <ViewMore type="button" onClick={onViewMore} disabled={loading}>
      View more
    </ViewMore>
  );

  const isEmpty = events.length === 0 && !loading;

  // Rendered before the list when nothing loaded, and AFTER the list when a
  // page-N "View more" fails (fix round 3): with prior events kept, the old
  // `isEmpty && error` gate made a failed page-2 fetch a completely silent
  // no-op — no message, no growth, no way to tell it from "no more pages".
  const errorNote = error && (
    <EmptyNote>
      Couldn&apos;t load activity.{' '}
      {onRetry && (
        <ViewMore type="button" onClick={onRetry}>
          Try again
        </ViewMore>
      )}
    </EmptyNote>
  );

  return (
    <Wrapper section={withHeader}>
      {withHeader && (
        <HeaderRow>
          <SectionTitle>Recent activity</SectionTitle>
          {viewMore}
        </HeaderRow>
      )}
      {isEmpty && errorNote}
      {isEmpty && !error && <EmptyNote>No score activity yet</EmptyNote>}
      <List>
        {events.map((event) => (
          <Entry key={event.id} data-testid={`feed-entry-${event.id}`}>
            <DescriptionRow>
              <AccentBar
                data-testid={`feed-accent-${event.id}`}
                style={{ backgroundColor: feedAccentColor(event.category) }}
              />
              <Description>{event.description}</Description>
            </DescriptionRow>
            <MetaRow>
              {/* title keeps the exact time reachable for audits — the visible
                  short date can't order same-day events */}
              <span title={new Date(event.occurred_at).toLocaleString()}>
                {shortDate(event.occurred_at)}
              </span>
              <MetaDot />
              {/* Humanized, not the raw enum: this rendered literally as
                  "Today • score_initialized" beside the already-readable
                  description. The identifier stays in `title` so it's still
                  greppable when someone is matching UI to a ledger row. */}
              <span title={event.event_type}>
                {humanizeEventType(event.event_type)}
              </span>
            </MetaRow>
          </Entry>
        ))}
      </List>
      {!isEmpty && errorNote}
      {!withHeader && viewMore}
    </Wrapper>
  );
};

export default ActivityFeed;
